// @ts-check
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import clsx from "clsx";

import {
  FullScreenModal,
  FullScreenModalBody,
  FullScreenModalHeader,
  TextTooltip,
} from "@transfr-inc/dashboard-components";
import { Color } from "@transfr-inc/dashboard-components/utils";

import {
  metricById,
  ScopeLevel,
  supportsBreakdownLevel,
  useMetricRegistry,
} from "@transfr-inc/dashboard-components/outcome-metrics";
import { isMultiOrgRole } from "@models/role";
import { useRole } from "@queries/account-selectors";

import "./raw-data-export.modal.scss";

/**
 * `FontAwesomeIcon` is typed for the free icon prefixes only, so a `fa-light`
 * (Pro) tuple trips the type-checker. Aliasing through `any` lets these cards use
 * the app's Pro icons the way the rest of the dashboard does.
 *
 * @type {any}
 */
const Icon = FontAwesomeIcon;

/**
 * Row-granularity levels for a CSV export. The value is the `level` sent to the
 * backend `CsvExportRequest`; `learner_per_sim` is the per-simulation breakdown.
 */
export const CsvExportLevel = Object.freeze({
  Organization: "organization",
  Classroom: "classroom",
  Learner: "learner",
  LearnerPerSim: "learner_per_sim",
});

/**
 * The `raw_data_granularity` vocabulary the outcomes event dictionary defines,
 * which names the per-simulation grain "simulation" rather than by its wire level.
 *
 * @type {Record<string, string>}
 */
const EXPORT_LEVEL_GRANULARITY = Object.freeze({
  [CsvExportLevel.Organization]: "organization",
  [CsvExportLevel.Classroom]: "classroom",
  [CsvExportLevel.Learner]: "learner",
  [CsvExportLevel.LearnerPerSim]: "simulation",
});

/**
 * @param {string} level - A `CsvExportLevel` value.
 * @returns {string|undefined} Undefined for an unrecognized level, so the event
 *   omits the grain rather than reporting an invented one.
 */
export function exportGranularity(level) {
  return EXPORT_LEVEL_GRANULARITY[level];
}

/**
 * Maps a `CsvExportLevel` to the `ScopeLevel` the metric registry keys its
 * `invalidBreakdownLevels` on, so a picked export level can be checked against
 * each metric's per-grain validity. The two enums don't share string values
 * (`organization` vs `org`). The per-simulation export is absent: it reports no
 * metrics, so no metric's grain validity bears on it.
 *
 * @type {Record<string, string>}
 */
const EXPORT_LEVEL_SCOPE = Object.freeze({
  [CsvExportLevel.Organization]: ScopeLevel.Org,
  [CsvExportLevel.Classroom]: ScopeLevel.Classroom,
  [CsvExportLevel.Learner]: ScopeLevel.Learner,
});

/**
 * The ids in `metricIds` that can be exported at `exportLevel`, in the given
 * order: the level's grain has to produce each one. Mirrors the backend's
 * per-grain metric guard, so a request built from this never carries a metric
 * the backend would reject with a 400; the modal warns about the ones dropped
 * instead. Every id passes for a level with no scope mapping (the per-simulation
 * grain reports no metrics).
 *
 * @param {readonly import("@transfr-inc/dashboard-components/outcome-metrics").MetricDefinition[]} registry
 * @param {string} exportLevel - A `CsvExportLevel` value.
 * @param {string[]} metricIds
 * @returns {string[]}
 */
export function exportableMetricIds(registry, exportLevel, metricIds) {
  const scopeLevel = EXPORT_LEVEL_SCOPE[exportLevel];
  if (scopeLevel == null) {
    return metricIds;
  }
  return metricIds.filter((id) =>
    supportsBreakdownLevel(registry, id, /** @type {any} */ (scopeLevel))
  );
}

/**
 * Labels of the metrics in `metricIds` that can't be exported at `exportLevel`,
 * in the given order; the complement of {@link exportableMetricIds}.
 *
 * @param {readonly import("@transfr-inc/dashboard-components/outcome-metrics").MetricDefinition[]} registry
 * @param {string} exportLevel - A `CsvExportLevel` value.
 * @param {string[]} metricIds
 * @returns {string[]}
 */
function unsupportedMetricLabels(registry, exportLevel, metricIds) {
  const exportable = new Set(
    exportableMetricIds(registry, exportLevel, metricIds)
  );
  return metricIds
    .filter((id) => !exportable.has(id))
    .map((id) => metricById(registry, id)?.label ?? id);
}

/**
 * Joins metric labels into a human clause: "A", "A and B", "A, B, and C".
 *
 * @param {string[]} labels
 * @returns {string}
 */
function joinLabels(labels) {
  if (labels.length <= 1) {
    return labels[0] ?? "";
  }
  if (labels.length === 2) {
    return `${labels[0]} and ${labels[1]}`;
  }
  return `${labels.slice(0, -1).join(", ")}, and ${labels.at(-1)}`;
}

/**
 * The export levels offered as cards, in display order. `Per Organization` is a
 * multi-org-only breakdown.
 */
const EXPORT_LEVEL_CARDS = [
  {
    level: CsvExportLevel.Organization,
    title: "Per Organization",
    description: "One row for every organization.",
    icon: ["fa-light", "building"],
    multiOrgOnly: true,
  },
  {
    level: CsvExportLevel.Classroom,
    title: "Per Classroom",
    description: "One row for every classroom.",
    icon: ["fa-light", "chalkboard-user"],
  },
  {
    level: CsvExportLevel.Learner,
    title: "Per Learner",
    description: "One row for every learner.",
    icon: ["fa-light", "user"],
  },
  {
    level: CsvExportLevel.LearnerPerSim,
    title: "Per Simulation",
    description: "One row per learner per simulation.",
    icon: ["fa-light", "head-side-goggles"],
  },
];

/**
 * Full-screen "Raw Data" export dialog. Presents the export row-granularity
 * levels as selectable cards; picking a card is the primary action — it emits
 * the chosen level and closes the dialog (there is no separate submit).
 *
 * The card set is gated by role: a multi-org viewer (Super Admin, Reporter) sees
 * all levels; a single-org viewer (Admin, Instructor) does not see
 * `Per Organization`.
 *
 * A metric the export will leave out — because the active module/program scope
 * can't narrow it, or because the level's grain can't produce it (e.g. Top
 * Careers per classroom) — is named in a non-blocking warning on the card, and
 * the export proceeds without it (see {@link exportableMetricIds}). A level is
 * disabled only when nothing would be left to export.
 *
 * @param {object} props
 * @param {boolean} props.open - Whether the dialog is shown.
 * @param {() => void} props.onClose - Called when the dialog is dismissed (back
 *   arrow, close X, or after a level is selected).
 * @param {(level: string) => void} props.onSelectLevel - Called with the chosen
 *   `CsvExportLevel` value when a selectable card is picked.
 * @param {string[]} [props.metricIds] - The metric ids the export will request;
 *   a level that can't produce any of them is disabled.
 * @param {string[]} [props.scopeExcludedLabels] - Labels of metrics the active
 *   module/program scopes exclude. With no metric left, the metric-driven levels
 *   are disabled.
 * @param {string[]} [props.scopeExcludedBy] - Names of the scopes doing the
 *   excluding ("module", "program"), in the order the copy reads them.
 */
export function RawDataExportModal({
  open,
  onClose,
  onSelectLevel,
  metricIds = [],
  scopeExcludedLabels = [],
  scopeExcludedBy = [],
}) {
  const registry = useMetricRegistry();
  const role = useRole();
  const multiOrg = role != null && isMultiOrgRole(role);

  const cards = EXPORT_LEVEL_CARDS.filter(
    (card) => !card.multiOrgOnly || multiOrg
  );

  const onCardClick = (card) => {
    onSelectLevel?.(card.level);
    onClose?.();
  };

  const scopeExcludedClause = joinLabels(scopeExcludedLabels);
  const excludedCount = scopeExcludedLabels.length;
  const noMetricsUnderScope = metricIds.length === 0 && excludedCount > 0;
  const filterClause = `${joinLabels(scopeExcludedBy)} filter${
    scopeExcludedBy.length > 1 ? "s" : ""
  }`;
  const exclusionHeadline =
    excludedCount === 1
      ? `1 metric excluded by your ${filterClause}`
      : `${excludedCount} metrics excluded by your ${filterClause}`;
  // A surviving metric has to satisfy every active scope, so the reason reads
  // as a conjunction.
  const scopeDataClause = joinLabels(
    scopeExcludedBy.map((name) => `${name}-level`)
  );

  return (
    <FullScreenModal
      open={open}
      onClose={onClose}
      className="raw-data-export-modal"
      drawerBgColor={Color.white}
    >
      <FullScreenModalHeader title="Raw Data" />
      <button
        type="button"
        className="raw-data-export-back"
        aria-label="Back"
        onClick={onClose}
      >
        <Icon icon={["fa-light", "arrow-left"]} />
      </button>
      <FullScreenModalBody>
        <div className="raw-data-export-canvas">
          <div className="raw-data-export-content">
            <h3 className="raw-data-export-heading">
              How would you like your spreadsheet formatted?
            </h3>
            <p className="raw-data-export-subheading">
              *Filters selected on your homepage will be applied to any reports
              generated.
            </p>

            <div className="raw-data-export-cards">
              {cards.map((card) => {
                // Only a metric-driven level loses columns: the per-simulation
                // grain reports no metrics.
                const isMetricDriven = EXPORT_LEVEL_SCOPE[card.level] != null;
                const dropsScopeMetrics = isMetricDriven && excludedCount > 0;
                const levelExcludedLabels = isMetricDriven
                  ? unsupportedMetricLabels(registry, card.level, metricIds)
                  : [];
                const dropsLevelMetrics = levelExcludedLabels.length > 0;
                const levelHeadline =
                  levelExcludedLabels.length === 1
                    ? `1 metric can't be exported ${card.title.toLowerCase()}`
                    : `${
                        levelExcludedLabels.length
                      } metrics can't be exported ${card.title.toLowerCase()}`;

                // One warning box, one entry per reason, in the order the export
                // applies them: the scope narrows the selection first, then the
                // level's grain.
                const warnings = [
                  dropsScopeMetrics && {
                    key: "scope",
                    headline: exclusionHeadline,
                    names: scopeExcludedClause,
                  },
                  dropsLevelMetrics && {
                    key: "level",
                    headline: levelHeadline,
                    names: joinLabels(levelExcludedLabels),
                  },
                ].filter(Boolean);

                const inner = (
                  <>
                    <Icon
                      className="raw-data-export-card-icon"
                      icon={card.icon}
                    />
                    <span className="raw-data-export-card-title">
                      {card.title}
                    </span>
                    <span className="raw-data-export-card-description">
                      {card.description}
                    </span>
                    {warnings.length > 0 && (
                      <span className="raw-data-export-card-warning">
                        {warnings.map((warning) => (
                          <span
                            key={warning.key}
                            className="raw-data-export-card-warning-item"
                          >
                            <span className="raw-data-export-card-warning-headline">
                              <Icon
                                icon={["fa-solid", "triangle-exclamation"]}
                              />
                              {warning.headline}
                            </span>
                            <span className="raw-data-export-card-warning-names">
                              {warning.names}
                            </span>
                          </span>
                        ))}
                      </span>
                    )}
                  </>
                );

                // The warning is non-blocking: a level stays selectable while
                // at least one metric survives both exclusions.
                const exportableCount =
                  metricIds.length - levelExcludedLabels.length;
                let disabledReason = null;
                if (noMetricsUnderScope && dropsScopeMetrics) {
                  disabledReason = `No selected metric has ${scopeDataClause} data, so this level has nothing to export.`;
                } else if (dropsLevelMetrics && exportableCount === 0) {
                  disabledReason =
                    "No selected metric can be exported at this level, so it has nothing to export.";
                }

                if (disabledReason) {
                  return (
                    <TextTooltip
                      key={card.level}
                      text={disabledReason}
                      className="raw-data-export-card-tooltip"
                    >
                      <span
                        role="button"
                        aria-disabled="true"
                        aria-label={`${card.title} (${disabledReason})`}
                        className="raw-data-export-card is-disabled"
                      >
                        {inner}
                      </span>
                    </TextTooltip>
                  );
                }

                return (
                  <button
                    key={card.level}
                    type="button"
                    className={clsx("raw-data-export-card")}
                    onClick={() => onCardClick(card)}
                  >
                    {inner}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </FullScreenModalBody>
    </FullScreenModal>
  );
}
